import { Keyword } from "../../../pages/filtering/types";
import SessionStore from "../../../stores/SessionStore";
import { RedFlagPillProps, RedFlagSeverity } from "../RedFlagPill/RedFlagPill";

export interface RedFlagRawApiData {
  data: RedFlagRawData;
}

// This is the data that comes back from the /riskselector API endpoint.
export interface RedFlagRawData extends Record<string, number | string | Record<string, number> | unknown[] | undefined> {
  name: string;
  most_recent_hit_datetime: string;
  adult_content_hit: number;
  offensive_hit: number;
  self_harm_hit: number;
  substance_abuse_hit: number;
  hate_speech_hit: number;
  violence_hit: number;
  academic_dishonesty_hit: number;
  extremism_hit: number;
  criminal_activity_hit: number;
  abuse_hit: number;
  vpn_hit: number;
  suicidal_search_term?: number;
  search_indicators?: Record<string, number>;
  devices?: unknown[];
}

export interface SignatureKeyword {
  id: string;
  name: string;
  deprecated: boolean;
}

export const FormatRedFlagDataForPills = (data: RedFlagRawData, keywords: SignatureKeyword[], user: string): RedFlagPillProps[] => {
  const result: RedFlagPillProps[] = [];
  const signatures = signatureRedFlagsByRegion();
  const defaultKeys: string[] = signatures.map((s) => s.id);

  for (const key of defaultKeys) {
    const value = data[key];
    if (value && typeof value === "number" && value > 0) {
      result.push({
        severity: getSeverity(value),
        text: `${value} ${getLabel(key, keywords)}`,
        link: getLink(key, user, keywords),
      });
    }
  }

  const devicesThreshold = 5;
  if (data.devices && data.devices.length > devicesThreshold) {
    const value = data.devices.length;
    result.push({
      severity: getSeverity(value),
      text: `${value} Devices`,
    });
  }

  if (data.search_indicators) {
    const indicators = data.search_indicators;
    Object.keys(indicators).forEach((key) => {
      const value = indicators[key];
      if (value && value > 0) {
        result.push({
          severity: getSeverity(value),
          text: `${value} ${getLabel(key, keywords)}`,
          link: getLink(key, user, keywords),
        });
      }
    });
  }

  return result;
};

export const getLabel = (id: string, keywords: SignatureKeyword[]): string => {
  const labelMap: Record<string, string> = {
    adult_content_hit: "Adult Content",
    offensive_hit: "Offensive Content",
    self_harm_hit: "Suicide",
    substance_abuse_hit: "Substance Abuse",
    hate_speech_hit: "Hate Speech",
    violence_hit: "Violence",
    academic_dishonesty_hit: "Academic Dishonesty",
    extremism_hit: "Radicalisation",
    criminal_activity_hit: "Criminal Activity",
    abuse_hit: "Abuse",
    vpn_hit: "VPN",
    devices: "Devices",
  };

  if (labelMap[id]) {
    return labelMap[id];
  }

  for (const keyword of keywords) {
    if (keyword.id === id) {
      if (keyword.name.toLowerCase().includes("search")) {
        return keyword.name;
      } else {
        return keyword.name + " Search";
      }
    }
  }
  return id;
};

export const getLink = (id: string, user: string, keywords: SignatureKeyword[]): string | undefined => {
  const filteredSignatures = signatureRedFlagsByRegion();

  // Handle special cases first
  switch (id) {
    case "suicidal_search_term":
      return `/cybersafety/search/user/${user}/suicide`;
    case "devices":
      return undefined;
  }

  // Check if the id is in filteredSignatures and return the standardized URL
  const signatureObj = filteredSignatures.find((s) => s.id === id);
  if (signatureObj) {
    return `/surfwize/reporting/type/${signatureObj.signature_id}/user/${user}?hits=true`;
  }

  // Handle custom keywords
  for (const keyword of keywords) {
    if (keyword.id === id) {
      return `/cybersafety/search/user/${user}/${keyword.name}`;
    }
  }

  return undefined;
};

const getSeverity = (count: number): RedFlagSeverity => {
  const highSeverityThreshold = 5;
  const mediumSeverityThreshold = 2;
  if (count > highSeverityThreshold) {
    return "high";
  }
  if (count > mediumSeverityThreshold) {
    return "medium";
  }
  return "low";
};

export const keywordRegionMatch = (keywords: Keyword[]): Keyword[] => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-assignment
  const regionValue = (SessionStore as any).getSelectedRegion() as string | null;
  if (!regionValue) {
    return keywords.filter((k) => !k.deprecated && (!k.regions || k.regions.length === 0));
  }
  let region = regionValue.toLowerCase().split("-")[0];
  region = region === "au" ? "syd" : region;
  region = region === "stg" ? "beta" : region;
  const filteredKeywords = keywords.filter((k) => !k.deprecated && (!k.regions || k.regions.length === 0 || k.regions.includes(region)));
  return filteredKeywords;
};

export const signatureRedFlagsByRegion = (): { id: string; signature_id: string; name: string }[] => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
  let region = ((SessionStore as any).getSelectedRegion() as string).toLowerCase().split("-")[0];
  region = region === "au" ? "syd" : region;
  region = region === "stg" ? "beta" : region;
  const signatures = [
    { id: "adult_content_hit", signature_id: "sphirewall.application.porn", name: "Adult Content" },
    { id: "offensive_hit", signature_id: "sphirewall.application.offensive", name: "Offensive Content" },
    { id: "vpn_hit", signature_id: "sphirewall.application.blocklist.proxies", name: "VPN" },
    { id: "abuse_hit", signature_id: "sphirewall.application.childporn", name: "Abuse" },
    { id: "hate_speech_hit", signature_id: "sphirewall.application.hate", name: "Hate Speech" },
    { id: "violence_hit", signature_id: "sphirewall.application.blocklist.violence", name: "Violence" },
    { id: "extremism_hit", signature_id: "sphirewall.category.terrorism", name: "Radicalisation" },
    { id: "criminal_activity_hit", signature_id: "sphirewall.application.illegal", name: "Criminal Activity" },
    { id: "self_harm_hit", signature_id: "sphirewall.category.selfharm", name: "Suicide" },
    { id: "substance_abuse_hit", signature_id: "sphirewall.application.substanceabuse", name: "Substance Abuse" },
    { id: "academic_dishonesty_hit", signature_id: "sphirewall.application.blocklist.schoolcheating", name: "Academic Dishonesty" },
  ];
  const filteredSignatures =
    region === "uk" || region === "beta"
      ? signatures
      : signatures.filter((s) => !["abuse_hit", "criminal_activity_hit", "extremism_hit"].includes(s.id));

  return filteredSignatures;
};

export const alertRedFlagsByRegion = (): { id: string; name: string }[] => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
  let region = (((SessionStore as any).getSelectedRegion() as string) || "").toLowerCase().split("-")[0];
  region = region === "au" ? "syd" : region;
  region = region === "stg" ? "beta" : region;
  const signatures = [
    { id: "adult_content_hit", name: "Adult Content" },
    { id: "abuse_hit", name: "Abuse" },
    { id: "extremism_hit", name: "Radicalisation" },
    { id: "criminal_activity_hit", name: "Criminal Activity" },
    { id: "self_harm_hit", name: "Suicide" },
  ];
  const alertRedFlags =
    region === "uk" || region === "beta"
      ? signatures
      : signatures.filter((s) => !["abuse_hit", "criminal_activity_hit", "extremism_hit", "self_harm_hit"].includes(s.id));

  return alertRedFlags;
};
